import React, { useEffect } from 'react';
import { Button, Form, Input, Select } from 'antd';
import type { FormInstance, FormListFieldData } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  HTS_CLASSIFICATION_TOPICS,
  HtsInputType,
  HtsTopicKind,
  HtsYesNoAnswer,
} from '@evaltick/shared';
import { cn } from '@/utils';
import {
  HARD_GATE_PRIORITY_OPTIONS,
  READINESS_PILLAR_OPTIONS,
  backfillGroupFieldKeys,
  isTargetableGroupField,
} from '../htsQuestionFormOptions';
import { BEHAVIOR_CARD_BY_VALUE } from '../htsQuestionWizardConfig';
import { SelectableOption } from '../SelectableOption';
import { StepHeader } from '../StepHeader';
import { HtsQuestionBehavior } from '../htsQuestionBehavior';
import type {
  HtsGroupFieldFormValue,
  HtsHardGateGroupConditionFormValue,
  HtsQuestionFormValues,
} from '../htsQuestionFormTypes';
import styles from '../HtsQuestionForm.module.scss';

interface OptionItem {
  label: string;
  value: string;
}

interface SingleChipsProps {
  value?: string;
  onChange?: (value: string) => void;
  options: OptionItem[];
  className?: string;
}

const SingleChips: React.FC<SingleChipsProps> = ({ value, onChange, options, className }) => (
  <div className={cn(styles.chipRow, className)}>
    {options.map((o) => (
      <SelectableOption key={o.value} variant="chip" selected={value === o.value} onClick={() => onChange?.(o.value)}>
        {o.label}
      </SelectableOption>
    ))}
  </div>
);

interface MultiChipsProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  options: OptionItem[];
  className?: string;
}

const MultiChips: React.FC<MultiChipsProps> = ({ value = [], onChange, options, className }) => (
  <div className={cn(styles.chipRow, className)}>
    {options.map((o) => {
      const selected = value.includes(o.value);
      return (
        <SelectableOption
          key={o.value}
          variant="chip"
          selected={selected}
          onClick={() => onChange?.(selected ? value.filter((v) => v !== o.value) : [...value, o.value])}
        >
          {o.label}
        </SelectableOption>
      );
    })}
  </div>
);

interface RowListProps {
  value?: string | null;
  onChange?: (value: string) => void;
  options: OptionItem[];
}

const RowList: React.FC<RowListProps> = ({ value, onChange, options }) => (
  <div className={styles.rowList}>
    {options.map((o) => (
      <SelectableOption key={o.value} variant="row" selected={value === o.value} onClick={() => onChange?.(o.value)}>
        {o.label}
      </SelectableOption>
    ))}
  </div>
);

interface HardGateConditionRowProps {
  form: FormInstance<HtsQuestionFormValues>;
  field: FormListFieldData;
  groupFields: HtsGroupFieldFormValue[];
  fieldOptions: OptionItem[];
  onRemove: (name: number) => void;
}

// Sentinel Select value for "None selected" — never collides with a real
// (slugified) row field key.
const NONE_SELECTED_OPTION_VALUE = '__noneSelected__';

// One row of the "flag if any row has…" condition list — the value widget
// adapts to whichever row field this condition targets. "None selected"
// shares the same dropdown but needs no field or value at all.
const HardGateConditionRow: React.FC<HardGateConditionRowProps> = ({
  form,
  field,
  groupFields,
  fieldOptions,
  onRemove,
}) => {
  const selectedKey: string | undefined = Form.useWatch(
    ['hardGateGroupConditions', field.name, 'groupFieldKey'],
    { form, preserve: true }
  );
  const noneSelected: true | undefined = Form.useWatch(
    ['hardGateGroupConditions', field.name, 'noneSelected'],
    { form, preserve: true }
  );
  const targetField = groupFields.find((f) => f.key === selectedKey);
  // Points at a row field deleted in step 3 — flag the Select, save is already blocked.
  const targetMissing = !noneSelected && Boolean(selectedKey) && !targetField;

  const conditionPath = ['hardGateGroupConditions', field.name] as const;

  // Retargeting (including to/from "None selected") clears only this row's value.
  const handleTargetChange = (nextValue: string) => {
    if (nextValue === NONE_SELECTED_OPTION_VALUE) {
      form.setFieldValue([...conditionPath, 'noneSelected'], true);
      form.setFieldValue([...conditionPath, 'groupFieldKey'], undefined);
      form.setFieldValue([...conditionPath, 'equals'], undefined);
    } else {
      form.setFieldValue([...conditionPath, 'noneSelected'], undefined);
      if (nextValue !== selectedKey) form.setFieldValue([...conditionPath, 'equals'], undefined);
      form.setFieldValue([...conditionPath, 'groupFieldKey'], nextValue);
    }
  };

  const selectOptions = [
    ...fieldOptions,
    { label: 'None selected (no rows added)', value: NONE_SELECTED_OPTION_VALUE },
  ];

  return (
    <div className={styles.optionRow}>
      <Select
        placeholder="Row field"
        options={selectOptions}
        className={styles.groupFieldType}
        popupMatchSelectWidth={false}
        value={noneSelected ? NONE_SELECTED_OPTION_VALUE : selectedKey}
        onChange={handleTargetChange}
        status={targetMissing ? 'error' : undefined}
        title={targetMissing ? 'This row field no longer exists — pick another' : undefined}
      />
      {noneSelected ? (
        <div className={styles.hardGateConditionValue} />
      ) : (
        <Form.Item
          {...field}
          name={[field.name, 'equals']}
          rules={[{ required: true, message: 'Choose the flagged value' }]}
          noStyle
        >
          {targetField?.inputType === HtsInputType.YES_NO ? (
            <SingleChips
              className={styles.hardGateConditionValue}
              options={[
                { label: 'Yes', value: HtsYesNoAnswer.YES },
                { label: 'No', value: HtsYesNoAnswer.NO },
              ]}
            />
          ) : targetField?.inputType === HtsInputType.SELECT || targetField?.inputType === HtsInputType.CHECKBOXES ? (
            <MultiChips
              className={styles.hardGateConditionValue}
              options={(targetField.options ?? []).filter(Boolean).map((o) => ({ label: o, value: o }))}
            />
          ) : (
            <Input placeholder="Exact answer text that triggers the flag" className={styles.hardGateConditionValue} />
          )}
        </Form.Item>
      )}
      <Button icon={<DeleteOutlined />} onClick={() => onRemove(field.name)} type="text" danger />
    </div>
  );
};

interface Step5ConfigureProps {
  form: FormInstance<HtsQuestionFormValues>;
  behavior: HtsQuestionBehavior | undefined;
  inputType: HtsInputType | undefined;
  ownOptions: string[] | undefined;
  /** This question's own REPEATABLE_GROUP row fields — only relevant when inputType is REPEATABLE_GROUP. */
  groupFields: HtsGroupFieldFormValue[] | undefined;
}

export const Step5Configure: React.FC<Step5ConfigureProps> = ({ form, behavior, inputType, ownOptions, groupFields }) => {
  const card = behavior ? BEHAVIOR_CARD_BY_VALUE[behavior] : undefined;
  const isRepeatableGroup = inputType === HtsInputType.REPEATABLE_GROUP;

  // Row fields carry no key until `buildGroupFields` assigns one at save, but
  // a condition must reference its target by key now — so materialise keys
  // as soon as this step needs them, pinning each down before a later label edit.
  useEffect(() => {
    if (!isRepeatableGroup) return;
    const keyed = backfillGroupFieldKeys(groupFields ?? []);
    if (keyed) form.setFieldValue('groupFields', keyed);
  }, [isRepeatableGroup, groupFields, form]);

  const groupFieldOptions = (groupFields ?? [])
    .filter(isTargetableGroupField)
    .map((f) => ({ label: f.label, value: f.key }));

  const classificationTopicOptions = HTS_CLASSIFICATION_TOPICS.filter(
    (t) => t.kind === (behavior === HtsQuestionBehavior.Readiness ? HtsTopicKind.Readiness : HtsTopicKind.Suitability)
  ).map((t) => ({ label: t.label, value: t.id }));

  return (
    <div>
      <StepHeader
        stepNumber={5}
        title={card ? `Configure: ${card.label}${card.sub ? ` ${card.sub}` : ''}` : 'Configure'}
        description={card?.detail}
      />

      {behavior === HtsQuestionBehavior.Sensitive && (
        <div className={styles.sensitiveNote}>
          Nothing more to set — this answer routes straight to the human evaluator, untouched by AI, and never
          feeds a score.
        </div>
      )}

      {behavior === HtsQuestionBehavior.RecordOnly && (
        <Form.Item
          name="readinessPillars"
          label="Feeds readiness pillars"
          extra="Optional. Which pillar(s) of the Readiness Score this answer is scored under."
        >
          <MultiChips options={READINESS_PILLAR_OPTIONS} />
        </Form.Item>
      )}

      {(behavior === HtsQuestionBehavior.Suitability || behavior === HtsQuestionBehavior.Readiness) && (
        <Form.Item
          name="classificationTopic"
          label={behavior === HtsQuestionBehavior.Readiness ? 'Follow-up topic *' : 'Classification topic *'}
          extra={
            behavior === HtsQuestionBehavior.Readiness
              ? 'Decides both what a follow-up asks about when the answer is too vague, and which Readiness pillar the answer is scored under — the topic is the pillar.'
              : "All classification topics roll up into a single Integrity pillar score — the choice only tells the AI what to check the answer for."
          }
          rules={[{ required: true, message: 'Choose a topic' }]}
        >
          <RowList options={classificationTopicOptions} />
        </Form.Item>
      )}

      {behavior === HtsQuestionBehavior.HardGate && (
        <>
          {isRepeatableGroup ? (
            <Form.List
              name="hardGateGroupConditions"
              rules={[
                {
                  validator: async (_, conditions: HtsHardGateGroupConditionFormValue[] | undefined) => {
                    if (!conditions || conditions.length === 0) {
                      return Promise.reject(new Error('Add at least one condition'));
                    }
                  },
                },
              ]}
            >
              {(fields, { add, remove }) => (
                <div className={styles.optionsList}>
                  <div className={styles.optionsHeaderRow}>
                    <label className={styles.optionsLabel}>Flag if any row has…</label>
                    <Button
                      type="default"
                      size="small"
                      icon={<PlusOutlined />}
                      className={cn(styles.btnSecondary, styles.btnSecondarySmall)}
                      onClick={() => add({})}
                    >
                      Add condition
                    </Button>
                  </div>
                  {fields.length === 0 && <div className={styles.optionsPanelEmpty}>Add at least one condition</div>}
                  {fields.map((field) => (
                    <HardGateConditionRow
                      key={field.key}
                      form={form}
                      field={field}
                      groupFields={groupFields ?? []}
                      fieldOptions={groupFieldOptions}
                      onRemove={remove}
                    />
                  ))}
                </div>
              )}
            </Form.List>
          ) : (
            <Form.Item
              name="hardGateEquals"
              label="Flagged Answer"
              rules={[{ required: true, message: 'Choose the answer value that triggers the flag' }]}
            >
              {inputType === HtsInputType.YES_NO ? (
                <SingleChips
                  options={[
                    { label: 'Yes', value: HtsYesNoAnswer.YES },
                    { label: 'No', value: HtsYesNoAnswer.NO },
                  ]}
                />
              ) : inputType === HtsInputType.SELECT || inputType === HtsInputType.CHECKBOXES ? (
                <MultiChips options={(ownOptions ?? []).filter(Boolean).map((o) => ({ label: o, value: o }))} />
              ) : (
                <Input placeholder="Exact answer text that triggers the flag" />
              )}
            </Form.Item>
          )}

          <Form.Item name="hardGatePriority" label="Priority" rules={[{ required: true, message: 'Choose a priority' }]}>
            <SingleChips options={HARD_GATE_PRIORITY_OPTIONS} />
          </Form.Item>

          <Form.Item
            name="hardGateReason"
            label="Reason shown to the evaluator"
            rules={[{ required: true, message: 'Explain why this flags' }]}
          >
            <Input placeholder="e.g. Under 19 — doesn't meet the minimum age requirement." />
          </Form.Item>
        </>
      )}
    </div>
  );
};

export default Step5Configure;
